import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError, fmtUSD } from "../../lib/api";
import { toast } from "sonner";
import { DealMemo } from "./DealMemo";
import { InterestBox } from "./InterestBox";
import { DealRoom } from "./DealRoom";
import { DealNotes } from "./DealNotes";

const ALLOCATION_BASIS =
  "Committing is binding on you if we allocate. It does not oblige us to allocate. Allocation is at Solera's discretion. We generally allocate in the order commitments are received. We may depart from that to avoid an allocation too small to administer, to limit concentration in a single member, or to prioritize a member who was cut on a recent deal.";

export default function DealDetail() {
  const { dealId } = useParams();
  const [deal, setDeal] = useState(null);
  const [amount, setAmount] = useState("");
  const [wire, setWire] = useState("");
  const [ack, setAck] = useState(false);
  const [qs, setQs] = useState([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(null);
  const [pos, setPos] = useState(null);

  const load = () => api.get(`/deals/${dealId}`).then((r) => setDeal(r.data)).catch(() => {});
  const loadQs = () => api.get(`/deals/${dealId}/questions`).then((r) => setQs(r.data)).catch(() => {});
  useEffect(() => { load(); loadQs(); }, [dealId]); // eslint-disable-line
  useEffect(() => { api.get("/my-terms").then((r) => setTerms(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`/deals/${dealId}/my-position`).then((r) => setPos(r.data)).catch(() => {}); }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deal?.my_commitment?.status === "allocated") {
      api.get("/settlement/instructions").then((r) => setWire(r.data.text)).catch(() => {});
    }
  }, [deal]);

  if (!deal) return <span className="t-label">Loading</span>;

  const gMin = terms?.effective?.min_commitment_usd;
  const effMin = gMin != null ? Math.min(deal.minimum_usd, gMin) : deal.minimum_usd;

  const ask = async () => {
    setBusy(true);
    try {
      await api.post(`/deals/${dealId}/questions`, { question });
      toast.success("Question sent to Solera. You will be notified when it is answered.");
      setQuestion("");
      loadQs();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmTransfer = async () => {
    setBusy(true);
    try {
      await api.post(`/commitments/${deal.my_commitment.commitment_id}/confirm-transfer`);
      toast.success("Noted. Solera confirms receipt within one business day.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deals/${dealId}/commit`, { amount_usd: Number(amount) });
      toast.success(`Commitment of ${fmtUSD(data.amount_usd)} recorded at ${new Date(data.created_at).toISOString().slice(0, 16).replace("T", " ")} UTC.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setInterest = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/deals/${dealId}/interest`, { action });
      setPos({ response: data.response, position: data.position, total_interested: data.total_interested });
      toast.success(action === "interested" ? "Interest registered. You are on the waitlist." : "Noted. You passed on this one.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadAttachment = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmed = deal.issuer_confirmation.toLowerCase().startsWith("received");

  return (
    <div className="rise" data-testid="deal-detail-page">
      <Link to="/app/deals" className="t-label hover:!text-[#E8E6E1]" data-testid="deal-back-link">Deals</Link>
      {deal.circle && <span className="t-label !text-[#C69A4E] ml-3" data-testid="deal-detail-circle">{deal.circle.name}</span>}

      <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-12 items-start">
        {/* Memo, light surface */}
        <DealMemo deal={deal} downloadAttachment={downloadAttachment} />

        {/* Terms + commit */}
        <div className="lg:sticky lg:top-8">
          <span className="t-label">Our view</span>
          <p className="t-small text-bone mt-2">{deal.view}</p>
          <span className="t-label block mt-6">Against that</span>
          <p className="t-small text-muted-s mt-2">{deal.against}</p>
          <InterestBox deal={deal} pos={pos} busy={busy} onInterest={setInterest} />
          {terms?.groups?.length > 0 && (
            <div className="mt-6 p-4" style={{ background: "#14171A", border: "1px solid #24272B" }} data-testid="deal-terms-note">
              <span className="t-label">Your terms via {terms.groups.map((g) => g.name).join(", ")}</span>
              <div className="mt-2 space-y-1">
                {terms.effective.finder_fee_percent != null && <p className="t-cap text-muted-s">Finders fee {terms.effective.finder_fee_percent}%</p>}
                {gMin != null && <p className="t-cap text-muted-s">Minimum {fmtUSD(gMin)}</p>}
                {terms.effective.priority_allocation && <p className="t-cap text-muted-s">Priority allocation</p>}
              </div>
            </div>
          )}

          <div className="hairline mt-8 pt-6 space-y-3">
            {[
              ["Last round", deal.last_round_valuation + ", announced " + deal.announced],
              ["Price per share", `USD ${deal.price_per_share.toFixed(2)}`],
              ["Minimum", fmtUSD(effMin)],
              ["Charges", `${deal.admin_charge}, ${deal.carry}`],
              ...(deal.spv ? [["Vehicle", deal.spv.name]] : []),
              ...(deal.spv && deal.spv.finders_fee_value > 0
                ? [["Finders fee", deal.spv.finders_fee_type === "percent"
                    ? `${deal.spv.finders_fee_value} percent of allocation`
                    : `${fmtUSD(deal.spv.finders_fee_value)} per transaction`]]
                : []),
              ["Closes", deal.closes_at],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6">
                <span className="t-label">{k}</span>
                <span className="fig t-small text-bone text-right">{v}</span>
              </div>
            ))}
            <div className="flex justify-between gap-6">
              <span className="t-label">Issuer confirmation</span>
              <span className={`fig t-small text-right ${confirmed ? "text-bone" : "status-blocked"}`} data-testid="issuer-confirmation">
                {deal.issuer_confirmation}
              </span>
            </div>
          </div>

          <div className="hairline mt-8 pt-6" data-testid="commit-panel">
            {deal.my_commitment ? (
              <div>
                <span className="t-label">Your commitment</span>
                <p className="fig text-bone text-2xl mt-2" data-testid="my-commitment-amount">{fmtUSD(deal.my_commitment.amount_usd)}</p>
                <p className="t-cap text-muted-s mt-2">
                  Recorded {new Date(deal.my_commitment.created_at).toISOString().slice(0, 16).replace("T", " ")} UTC.
                  Status: {deal.my_commitment.status.replace("_", " ")}.
                </p>
                {deal.my_commitment.status === "allocated" && (
                  <div className="hairline mt-6 pt-5" data-testid="settlement-panel">
                    <span className="t-label">
                      Allocated {fmtUSD(deal.my_commitment.allocated_amount != null ? deal.my_commitment.allocated_amount : deal.my_commitment.amount_usd)}. Wire instructions
                    </span>
                    <pre className="fig t-small text-bone mt-3 whitespace-pre-wrap" style={{ fontFamily: "var(--font-mono)" }} data-testid="wire-instructions">{wire}</pre>
                    <button className={`option-card mt-4 !p-4 ${ack ? "selected" : ""}`} onClick={() => setAck(!ack)} data-testid="settlement-ack-toggle">
                      <span className="t-small">I agree to the subscription terms of this allocation and will wire from an account in my own name.</span>
                    </button>
                    <button className="btn-primary w-full mt-3" disabled={!ack || busy} onClick={confirmTransfer} data-testid="confirm-transfer-button">
                      Confirm funds sent
                    </button>
                  </div>
                )}
                {deal.my_commitment.status === "funds_sent" && (
                  <p className="t-small text-muted-s mt-4" data-testid="funds-sent-note">
                    Funds marked sent{deal.my_commitment.funds_sent_at ? ` ${deal.my_commitment.funds_sent_at.slice(0, 10)}` : ""}. Solera
                    confirms receipt within one business day.
                  </p>
                )}
                {deal.my_commitment.status === "settled" && (
                  <p className="t-small text-muted-s mt-4" data-testid="settled-note">
                    Settled. Your position appears in the portfolio once the transfer is recognized on the issuer's books.
                  </p>
                )}
              </div>
            ) : deal.status === "open" ? (
              <div>
                <p className="t-cap text-muted-s" data-testid="allocation-basis">{ALLOCATION_BASIS}</p>
                <input
                  className="input-solera mt-4 fig" type="number" min={effMin} step="1000"
                  placeholder={`Amount, USD. Minimum ${effMin.toLocaleString()}`}
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  data-testid="commit-amount-input"
                />
                <button className="btn-primary w-full mt-3" disabled={busy || !amount}
                  onClick={commit} data-testid="commit-button">Commit</button>
              </div>
            ) : (
              <p className="t-small text-muted-s">This allocation is closed.</p>
            )}
          </div>
        </div>
      </div>

      <DealRoom qs={qs} question={question} setQuestion={setQuestion} onAsk={ask} busy={busy} />
      {deal.can_add_notes && <DealNotes dealId={dealId} />}
    </div>
  );
}
